import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, BookOpen, LogOut, Trash2 } from 'lucide-react';
import type { ChatMessage } from '../components/ChatBubble';
import { DateInputsField } from '../components/DateInputs';
import { LanguageSelector } from '../components/LanguageSelector';
import { cn } from '../lib/cn';
import { chatHistoryQueryKey } from '../lib/api/chatHistory';
import { usePartner, useUpdateProfile, useUser } from '../lib/api/useUser';
import {
  PARTY_WITH_DOG_OPTIONS,
  validateName,
  validateStayDates,
  type Party,
  type Profile,
} from '../lib/onboarding';
import { resolvePersona } from '../lib/personas';
import {
  getPushStatus,
  PushError,
  subscribeToPush,
  unsubscribeFromPush,
  type PushStatus,
} from '../lib/push';
import {
  clearAllAppStorage,
  readChatHistory,
  writeChatHistory,
} from '../lib/storage';
import { getLocalDateString } from '../lib/teatime';
function timeNow(): string {
  return new Date().toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
}
/**
 * Order-insensitive equality for the editable subset of Profile.
 *
 * `name` compared as `(a.name ?? '') !== (b.name ?? '')` so a guest
 * who had no name and now still has no name reads as unchanged,
 * regardless of whether the underlying field is absent or undefined.
 * Equally, clearing the field (input → empty) compares against the
 * original `name` (a defined string) and triggers a save.
 *
 * Allergies dropped from the comparison 3 Jun 2026 (descope).
 */
function editableProfileEquals(a: Profile, b: Profile): boolean {
  if ((a.name ?? '') !== (b.name ?? '')) return false;
  if (a.party !== b.party) return false;
  if (a.hasDog !== b.hasDog) return false;
  if (a.stayStart !== b.stayStart) return false;
  if (a.stayEnd !== b.stayEnd) return false;
  return true;
}
export default function SettingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signOut } = useClerk();
  // `userId` (10 Jun 2026 structural fix) — Clerk user id, used to
  // scope the localStorage chat history read/write in the profile-
  // update ack path below. Without this, a guest editing their
  // details on a device that previously hosted another account would
  // append the persona ack onto the previous user's chat history
  // (shared `ti:chatHistory` key), and the previous chat could leak
  // into the current user's /me view on next render.
  //
  // `getToken` (12 Jun 2026, Batch 4 slice 4b) — Clerk JWT getter
  // required by the push subscribe/unsubscribe API calls. Also used
  // by the Clear Chat History handler (Batch 6, 16 Jun 2026) to
  // authenticate the DELETE /v1/chat/history call.
  const { userId, getToken } = useAuth();
  const { t } = useTranslation('common');
  // Source of truth: GET /v1/users/me via TanStack Query (post-25c-2).
  // RequireAuth gates this route, so by the time SettingsPage mounts
  // the query is warm. Cold cache flashes blank for one tick before
  // content paints.
  const { data: user, isLoading, isError } = useUser();
  // Partner attribution shares the same ['users','me', userId] cache as
  // useUser (select pattern), so this costs no extra round-trip. Drives
  // partner-aware persona resolution below.
  const { data: partner } = usePartner();
  const updateProfileMutation = useUpdateProfile();
  // Resolve the EFFECTIVE persona — partner persona (Ollie at Harlyn,
  // Chough at Cornish Gem) when the user is partner-attributed, else the
  // town persona (Morwenna for Padstow). MUST match how MePage and
  // MemoryPage resolve it: a partner guest has to see their own site's
  // persona on EVERY surface, settings included. Using the town-only
  // getPersona here leaked Morwenna branding into a Harlyn guest's
  // settings screen — a cross-site bleed. resolvePersona closes it.
  const persona = useMemo(
    () => (user ? resolvePersona(user.townSlug, partner ?? null) : null),
    [user, partner],
  );
  // Hooks must be called unconditionally. State is seeded from the
  // API-supplied profile if present, else placeholder values used only
  // if the profile-form section renders (it doesn't when `original` is null).
  const original: Profile | null = user?.profile ?? null;
  const [name, setName] = useState<string>(original?.name ?? '');
  const [party, setPartyState] = useState<Party>(original?.party ?? 'solo');
  const [hasDog, setHasDog] = useState<boolean>(original?.hasDog ?? false);
  const [stayStart, setStayStart] = useState<string>(original?.stayStart ?? '');
  const [stayEnd, setStayEnd] = useState<string>(original?.stayEnd ?? '');
  const nameId = useId();
  const nameErrorId = useId();
  /**
   * Push notifications state — Batch 4 slice 4b step 3.
   *
   * `pushStatus` is null until the on-mount status read resolves.
   * `getPushStatus()` is fully local (no network), so the load is
   * fast — but rendering nothing during the brief window prevents a
   * flash of incorrect copy (e.g. "off" before we know it's actually
   * "blocked"). After resolve, the section paints its real state.
   */
  const [pushStatus, setPushStatus] = useState<PushStatus | null>(null);
  const [isPushChanging, setIsPushChanging] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  /**
   * Clear-chat-history state — Batch 6 (16 Jun 2026).
   *
   * `isClearingChat` toggles the button to its loading copy while the
   * DELETE round-trip is in flight; `clearChatError` carries a
   * persona-neutral error message rendered inline below the button on
   * non-2xx responses or thrown exceptions. Mirrors the
   * `isPushChanging` / `pushError` pattern above for consistency.
   *
   * Local component state rather than a TanStack mutation hook because
   * this is a one-shot destructive action with no cache-write side
   * effects beyond an invalidate — closer in shape to the bare
   * `subscribeToPush` / `unsubscribeFromPush` helper pattern from
   * `lib/push.ts` than to the profile-PATCH `useUpdateProfile`
   * mutation, which has an `onSuccess` cache writer to keep.
   */
  const [isClearingChat, setIsClearingChat] = useState(false);
  const [clearChatError, setClearChatError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void getPushStatus()
      .then((status) => {
        if (!cancelled) setPushStatus(status);
      })
      .catch(() => {
        // Defensive: getPushStatus is internally guarded against the
        // common failure modes, but if anything unexpected surfaces
        // render the "unsupported" branch rather than hanging.
        if (!cancelled) {
          setPushStatus({
            support: 'unsupported',
            permission: 'default',
            subscribed: false,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const handlePushToggle = useCallback(async () => {
    if (!pushStatus || pushStatus.support !== 'supported') return;
    setIsPushChanging(true);
    setPushError(null);
    try {
      if (pushStatus.subscribed) {
        await unsubscribeFromPush(getToken);
      } else {
        await subscribeToPush(getToken);
      }
      const fresh = await getPushStatus();
      setPushStatus(fresh);
    } catch (err) {
      // Refresh status regardless — permission may have changed
      // mid-flow (e.g. user denied the browser prompt).
      const fresh = await getPushStatus();
      setPushStatus(fresh);
      // For permission_denied we don't surface an additional error
      // banner — the refreshed status will render the "blocked" help
      // text branch below, which is the right UX.
      if (!(err instanceof PushError && err.code === 'permission_denied')) {
        setPushError(t('push.error'));
      }
    } finally {
      setIsPushChanging(false);
    }
  }, [pushStatus, getToken, t]);
  /**
   * Clear chat history (Batch 6, 16 Jun 2026).
   *
   * Calls DELETE /v1/chat/history on the API — same endpoint that the
   * server-side `softDeleteAllMessages` helper writes against — then
   * wipes the localStorage chat cache for this user and navigates back
   * to /me, where the now-empty conversation renders against the empty
   * server + empty local cache.
   *
   * Why all three steps:
   *   1. Server DELETE soft-deletes the user's `chat_messages` rows
   *      (deleted_at = now()), which is the durable source of truth.
   *      Without this, signing out and back in would re-fetch the
   *      conversation from the DB.
   *   2. Local storage clear — without this, MePage's hydration would
   *      fold the stale local cache back over the empty server result
   *      via `reconcileHistory`, and the chat would render with the
   *      old messages anyway. `writeChatHistory([], userId)` is the
   *      canonical "I have no chat" state for a logged-in user.
   *   3. TanStack cache seed + invalidate — uses the identity-scoped
   *      query key (chatHistoryQueryKey(userId) → ['chat', 'history',
   *      userId]) that useChatHistory writes against. Seeding the
   *      empty result first makes the cleared state instant on next
   *      mount (no flash of pre-delete content from the cache);
   *      invalidating afterwards forces a fresh fetch on subscription
   *      so the cache stays honest if any other surface mounts the
   *      same query.
   *
   *      Bug fix (16 Jun 2026 follow-up): the original Batch 6 ship
   *      invalidated against ['chatHistory'], which is NOT a prefix
   *      of the canonical key (first segment is 'chat', not
   *      'chatHistory') and silently no-op'd under TanStack's
   *      partial-prefix matcher. MePage rehydrated against the
   *      pre-delete cached server response, the deleted messages
   *      re-rendered, and writeChatHistory inside MePage's persist
   *      effect saved them back to localStorage — locking the bug in
   *      past the 30s staleTime window. The identity-scoped key
   *      below is the canonical one useChatHistory writes against,
   *      so the cache hit aligns and the seed-then-invalidate
   *      sequence does what the docstring above always claimed.
   *
   * `window.confirm()` rather than a custom modal — single destructive
   * action, no dependency on a dialog component, works identically
   * across the supported browsers including iOS PWA. Persona name
   * interpolated into the confirmation copy so a Harlyn guest sees
   * "with Ollie" and a Padstow guest sees "with Morwenna".
   *
   * Error handling mirrors the push-toggle pattern: log to console for
   * forensics, set an inline error string for the user, leave them on
   * /settings so they can retry. We don't roll back the localStorage
   * clear on a server failure because the server might have succeeded
   * before the response was lost (e.g. flaky wifi mid-flight) — better
   * to be eventually-consistent toward "clean" than to retain stale
   * local data after a hopefully-successful server delete.
   *
   * The API base URL is read from VITE_API_URL (set per environment in
   * Cloudflare Pages build vars), with the production URL as a literal
   * fallback. The fallback matches the URL the DevTools fetch test
   * used during smoke verification on 16 Jun 2026.
   */
  const handleClearChat = useCallback(async () => {
    if (!user || !persona) return;
    const confirmMessage = t('settingsPage.clearChat.confirm', {
      personaName: persona.personaName,
    });
    if (!window.confirm(confirmMessage)) return;
    setIsClearingChat(true);
    setClearChatError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('No auth token available');
      const apiUrl =
        (import.meta.env.VITE_API_URL as string | undefined) ??
        'https://api.towninsider.co.uk';
      const response = await fetch(`${apiUrl}/v1/chat/history`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(
          `DELETE /v1/chat/history failed: ${response.status}`,
        );
      }
      // Clear localStorage cache for this user so MePage's
      // rehydration on next mount doesn't fold the now-stale local
      // cache back over the empty server result via reconcileHistory.
      writeChatHistory([], userId);
      // Seed the TanStack cache with the empty HistoryResponse
      // against the identity-scoped query key useChatHistory writes
      // against (chatHistoryQueryKey(userId) → ['chat', 'history',
      // userId]), then mark it stale so any subscribers refetch on
      // next mount. Coalesce userId to null to match
      // chatHistoryQueryKey's signature; useChatHistory itself does
      // the same coalesce on the read side so the keys align. See
      // the docstring above for the bug-fix history.
      const historyKey = chatHistoryQueryKey(userId ?? null);
      queryClient.setQueryData(historyKey, {
        messages: [],
        hasMore: false,
      });
      queryClient.invalidateQueries({ queryKey: historyKey });
      // Navigate to /me — the page mounts against the seeded empty
      // cache + empty server, renders the persona's clean opening
      // state with no flash of pre-delete content.
      navigate('/me');
    } catch (err) {
      console.error('[SettingsPage] handleClearChat failed', err);
      setClearChatError(t('settingsPage.clearChat.error'));
    } finally {
      setIsClearingChat(false);
    }
  }, [user, persona, userId, getToken, queryClient, navigate, t]);
  /**
   * Sign-out flow (revised 15 Jun 2026 — Bug 6 fix):
   *   1. Compute the redirect destination from the current user. EVERY
   *      guest is returned to their OWN site's branded join page so the
   *      experience stays standalone end-to-end:
   *        - partner-attributed → /join/{town}?ref={partner}
   *          (Harlyn Sands' Ollie hero, Cornish Gem's Chough, etc.)
   *        - bare town guest     → /join/{town}
   *          (the town persona's hero — Morwenna for Padstow)
   *      Read BEFORE the cache clear since `user` lives in the TanStack
   *      cache.
   *   2. Clear all `ti:*` localStorage so no app state survives
   *   3. Clear the TanStack Query cache so no fetched user data survives
   *   4. Hand control to Clerk via `signOut({ redirectUrl })`, which
   *      invalidates the session AND triggers a FULL-PAGE navigation
   *      to the redirect target.
   *
   * Order matters — clear our storage BEFORE Clerk's signOut so any
   * mid-flight components reading state during the un-auth transition
   * see a clean slate.
   *
   * Why a full reload (15 Jun 2026 — Bug 6 fix):
   *   The previous pattern — `await signOut()` then `navigate()` —
   *   kept React, the Clerk SDK, and TanStack Query alive in memory
   *   across the transition. Clerk's internal session state was
   *   updated server-side, but reads that landed in the same
   *   microtask before React re-rendered saw the pre-signOut view.
   *   The visible symptom: JoinPage's `isSignedIn` flickered true on
   *   first paint, the signed-in notice failed to render (user not
   *   yet loaded by useUser), and when the guest tapped "Open my
   *   page" the lazy-first-touch attribution fired as the ORIGINAL
   *   user — they silently re-entered their own account with the
   *   same userId. Hence the user-id-stable-across-sign-out cycle
   *   observed during 15 Jun 2026 demo-prep testing.
   *
   *   `signOut({ redirectUrl })` hands control to Clerk, which
   *   invalidates the session, clears its cookies, and triggers a
   *   `window.location` navigation. The browser reloads, Clerk's SDK
   *   reinitialises from scratch, and the new page mounts with
   *   `isSignedIn` cleanly false. Matches the pattern used by
   *   `JoinPage.handleSignOut` ("use a different account") since
   *   10 Jun 2026, which is locked by the sign-out cleanup test in
   *   `JoinPage.test.tsx`.
   *
   * Standalone-site branding (11 Jun 2026): a partner guest is returned
   * to their PARTNER-branded join page (ref intact), so signing out of
   * Harlyn lands on Harlyn — NEVER on the town's Morwenna page. An
   * earlier fix dropped the ref to stop a leak; that was the wrong
   * layer and made Harlyn sign-out land on Padstow/Morwenna. The leak
   * is now closed where it belongs — on the SERVER: partner_ref is
   * first-touch immutable, so the ref re-seeded into localStorage by
   * this branded redirect can no longer re-attribute the next guest who
   * signs in on this device (/invitation/complete ignores any ref for
   * an existing user). Carrying the ref for branding is therefore safe,
   * and every site stays self-contained.
   *
   * partnerRef is encodeURIComponent-wrapped defensively. Partner slugs
   * are bounded by a strict regex server-side so encoding is currently
   * a no-op, but the wrap protects against any future slug widening and
   * against a CodeQL "URL construction from user input" finding.
   *
   * Failure handling: `signOut` failure is rare but possible (network
   * blip, Clerk SDK error). The catch falls back to SPA navigation so
   * the user gets off the settings page rather than stuck. State may
   * be inconsistent until the next reload, but the storage/cache
   * clears above have already run so nothing user-identifying
   * persists in app state.
   */
  const handleSignOut = useCallback(async () => {
    // `user` is non-null here — the Sign Out button only renders after
    // the user/persona null-guard below. Narrow for TS.
    if (!user) return;
    // Each guest returns to THEIR OWN site's branded join page —
    // partner hero for partner guests, town hero otherwise. The ref is
    // safe to carry because partner_ref is immutable server-side (see
    // the standalone-site note above).
    const redirectUrl =
      user.partnerRef !== null
        ? `/join/${user.townSlug}?ref=${encodeURIComponent(user.partnerRef)}`
        : `/join/${user.townSlug}`;
    // Wipe browser-keyed client state BEFORE Clerk's signOut so any
    // component reading state mid-transition sees a clean slate.
    clearAllAppStorage();
    queryClient.clear();
    try {
      // Hand control to Clerk: invalidates the session, clears its
      // cookies, triggers a full-page navigation. See the "Why a full
      // reload" section above for the load-bearing rationale.
      await signOut({ redirectUrl });
    } catch (err) {
      // Rare path — Clerk SDK / network failure. Fall back to SPA nav
      // so the user gets off /settings. The storage/cache clears
      // above have already run.
      console.error('[SettingsPage] signOut failed', err);
      navigate(redirectUrl, { replace: true });
    }
  }, [signOut, navigate, queryClient, user]);
  // Brief blank frame on cold cache; almost never visible in normal
  // navigation since OnboardingCompletePage warms it.
  if (isLoading) return null;
  // Bounce to root on query error, missing user, or unrecognised town.
  // RequireAuth covers the no-session case upstream.
  if (isError || !user || !persona) {
    return <Navigate to="/" replace />;
  }
  // Name field — empty input means "no name persisted". Trim before
  // composing the draft so leading/trailing whitespace doesn't sneak
  // into the wire payload or trigger a spurious "hasChanges" diff.
  //
  // validateName treats empty as null (valid — represents skip), so
  // the error UI only surfaces when the user has typed something that
  // doesn't match the charset. The save gate uses the same validator.
  const trimmedName = name.trim();
  const nameError = validateName(trimmedName);
  const showNameError = nameError !== null;
  // Profile-form draft + save logic, only meaningful when `original` exists.
  //
  // Conditional spread on `name` so an empty input persists as "field
  // absent" rather than `name: ''` (which the server's strict regex
  // would reject). Clearing the name from a profile that had one
  // therefore submits a payload WITHOUT the name field — the server's
  // full-replacement PATCH semantics drop the previous value.
  const draft: Profile = {
    stayStart,
    stayEnd,
    party,
    hasDog,
    ...(trimmedName.length > 0 ? { name: trimmedName } : {}),
  };
  const hasChanges =
    original !== null && !editableProfileEquals(original, draft);
  const stayDatesError =
    original !== null ? validateStayDates(stayStart, stayEnd) : null;
  const isSaving = updateProfileMutation.isPending;
  const canSave =
    hasChanges && stayDatesError === null && nameError === null && !isSaving;
  /**
   * Save flow (post-25c-2):
   *   1. PATCH /v1/users/me/profile via useUpdateProfile mutation
   *   2. On success: mutation's onSuccess writes the updated user
   *      record straight into the ['users', 'me', userId] cache, so
   *      /me sees the fresh profile on next render with no extra fetch
   *   3. Append persona-voiced ack to chat history (still localStorage —
   *      server-side chat history lands with API Phase 3+)
   *   4. Navigate to /me
   *
   * On error: don't navigate, don't append the ack — the ack only
   * appears if the write actually persisted. Inline error message
   * below the Save button gives the user a clear retry path; tapping
   * Save again re-fires the mutation.
   *
   * `readChatHistory(userId)` / `writeChatHistory(..., userId)` write
   * to the per-user scoped key `ti:chatHistory:{userId}` (10 Jun 2026
   * structural fix). The ack lands in the current user's chat
   * history, not a shared key — so editing details under one account
   * cannot leak a "details updated" message into another account's
   * chat surface.
   */
  const handleSave = async () => {
    if (!canSave) return;
    try {
      await updateProfileMutation.mutateAsync(draft);
    } catch {
      // Mutation error state is read from updateProfileMutation.isError
      // below; no extra UI state needed.
      return;
    }
    const ackMsg: ChatMessage = {
      id: `profile-update-${Date.now()}`,
      from: 'persona',
      text: persona.profileUpdatedAck(draft),
      timestamp: timeNow(),
      dateLocal: getLocalDateString(),
      kind: 'profile-update',
      status: 'sent',
    };
    const existing = readChatHistory(userId) ?? [];
    writeChatHistory([...existing, ackMsg], userId);
    navigate('/me');
  };
  const chipBase =
    'rounded-full px-4 py-2 text-sm font-medium ring-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2';
  const chipUnselected =
    'bg-cream-100 text-moor-900 ring-moor-200 hover:bg-moor-50';
  const chipSelected = 'bg-moor-700 text-cream-50 ring-moor-700';
  // Header label: partner name for partner guests (matches MePage), the
  // town "Insider" label otherwise. Keeps the settings header on-brand
  // for the guest's own site instead of always showing the town.
  const headerLabel = partner?.name ?? `${persona.townLabel} Insider`;
  return (
    <>
      <Helmet>
        <title>{`${t('settingsPage.title')} · ${headerLabel}`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="fixed inset-0 flex flex-col bg-cream-50">
        <header
          className="border-b border-moor-100 bg-cream-50/95 backdrop-blur"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="container-narrow flex items-center gap-3 px-4 py-3">
            <Link
              to="/me"
              aria-label={t('settingsPage.backToChatAriaLabel')}
              className="flex h-9 w-9 items-center justify-center rounded-full text-moor-700 transition-colors hover:bg-moor-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-wide text-moor-500">
                {headerLabel}
              </p>
              <h1 className="truncate text-base font-semibold text-moor-900">
                {t('settingsPage.title')}
              </h1>
            </div>
          </div>
        </header>
        <main id="main-content" className="min-h-0 flex-1 overflow-y-auto">
          <div className="container-narrow space-y-8 px-4 py-6">
            {original ? (
              <>
                {/*
                  Name — first form section, mirroring the onboarding
                  order (name → dates → party). Optional:
                  empty input means "no name persisted" and the field
                  is omitted from the PATCH payload via the conditional
                  spread on `draft` above. Clearing the field on a
                  profile that previously had a name therefore drops
                  the name server-side (full-replacement PATCH).
                */}
                <section>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-moor-500">
                    {t('settingsPage.yourName.sectionTitle')}
                  </h2>
                  <p className="mt-1 text-sm text-moor-600">
                    {t('settingsPage.yourName.sectionDescription', {
                      personaName: persona.personaName,
                    })}
                  </p>
                  <div className="mt-3">
                    <label htmlFor={nameId} className="sr-only">
                      {t('settingsPage.yourName.label')}
                    </label>
                    <input
                      id={nameId}
                      type="text"
                      value={name}
                      maxLength={50}
                      placeholder={t('settingsPage.yourName.placeholder')}
                      onChange={(e) => setName(e.target.value)}
                      autoComplete="given-name"
                      autoCapitalize="words"
                      aria-invalid={showNameError}
                      {...(showNameError
                        ? { 'aria-describedby': nameErrorId }
                        : {})}
                      className="w-full rounded-lg border border-moor-200 bg-cream-50 px-3 py-2 text-base text-moor-900 focus:border-moor-500 focus:outline-none focus:ring-2 focus:ring-moor-500/40 sm:max-w-xs"
                    />
                    {showNameError && nameError !== null && (
                      <p
                        id={nameErrorId}
                        role="alert"
                        className="mt-2 text-sm text-red-700"
                      >
                        {nameError}
                      </p>
                    )}
                  </div>
                </section>
                <section>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-moor-500">
                    {t('settingsPage.yourWeek')}
                  </h2>
                  <div className="mt-3">
                    <DateInputsField
                      stayStart={stayStart}
                      stayEnd={stayEnd}
                      onChange={(s, e) => {
                        setStayStart(s);
                        setStayEnd(e);
                      }}
                      error={stayDatesError}
                    />
                  </div>
                </section>
                <section>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-moor-500">
                    {t('settingsPage.whosWithYou.sectionTitle')}
                  </h2>
                  <div
                    role="radiogroup"
                    aria-label={t('settingsPage.whosWithYou.ariaLabel')}
                    className="mt-3 flex flex-wrap gap-2"
                  >
                    {PARTY_WITH_DOG_OPTIONS.map((opt) => {
                      const isSelected =
                        opt.value.party === party &&
                        opt.value.hasDog === hasDog;
                      // Chip label i18n (14 Jun 2026, Phase 4b-final-A).
                      // Inline switch on the typed discriminator
                      // (opt.value.party + opt.value.hasDog) instead of
                      // a dynamic key constructed from string
                      // concatenation. The strict t() augmentation
                      // (src/types/i18next.d.ts) wouldn't type-check
                      // `t(\`whosWithYou.options.\${variant}\`)` — the
                      // template-literal expression isn't statically
                      // resolvable to a known key. Eight explicit
                      // branches give us strict typing + exhaustiveness
                      // (Party union is closed) at the cost of a few
                      // lines of repetition. Keeps src/lib/onboarding.ts
                      // pure (no React/i18n imports) — the lib's
                      // `label` field is now used only as a stable
                      // React key here.
                      const label = (() => {
                        switch (opt.value.party) {
                          case 'solo':
                            return opt.value.hasDog
                              ? t('whosWithYou.options.soloWithDog')
                              : t('whosWithYou.options.solo');
                          case 'couple':
                            return opt.value.hasDog
                              ? t('whosWithYou.options.coupleWithDog')
                              : t('whosWithYou.options.couple');
                          case 'family':
                            return opt.value.hasDog
                              ? t('whosWithYou.options.familyWithDog')
                              : t('whosWithYou.options.family');
                          case 'group':
                            return opt.value.hasDog
                              ? t('whosWithYou.options.groupWithDog')
                              : t('whosWithYou.options.group');
                        }
                      })();
                      return (
                        <button
                          key={opt.label}
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          onClick={() => {
                            setPartyState(opt.value.party);
                            setHasDog(opt.value.hasDog);
                          }}
                          className={cn(
                            chipBase,
                            isSelected ? chipSelected : chipUnselected,
                          )}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </section>
                {/*
                  Memory Page link. Visually separated from the editable
                  sections by a thin rule — this isn't a setting, it's a
                  destination. Token uses user.townSlug (the route param
                  for /memories/:token mirrors the town slug in v1; real
                  per-user tokens land with API Phase 8 public sharing).
                */}
                <section className="border-t border-moor-100 pt-8">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-moor-500">
                    {t('settingsPage.memoryPage.sectionTitle')}
                  </h2>
                  <p className="mt-1 text-sm text-moor-600">
                    {t('settingsPage.memoryPage.sectionDescription', {
                      townLabel: persona.townLabel,
                      personaName: persona.personaName,
                    })}
                  </p>
                  <Link
                    to={`/memories/${user.townSlug}`}
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-cream-100 px-4 py-2 text-sm font-medium text-moor-900 ring-1 ring-moor-200 transition-colors hover:bg-moor-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
                  >
                    <BookOpen className="h-4 w-4" aria-hidden="true" />
                    {t('settingsPage.memoryPage.viewButton')}
                  </Link>
                </section>
              </>
            ) : (
              <section>
                <p className="text-sm text-moor-600">
                  {t('settingsPage.onboardingIncomplete', {
                    personaName: persona.personaName,
                  })}
                </p>
              </section>
            )}
            {/*
              Language — Phase 4b-3 (14 Jun 2026). Always visible
              regardless of profile state. Sits BEFORE Notifications so
              the two communication-preference sections cluster together
              and a guest can set their preferred language before
              reading the Notifications copy. Auto-detection from
              navigator.language already runs at app load via
              i18next-browser-languagedetector (Phase 4a); this surface
              is the durable override + manual confirmation home.
              The selector is a native <select> wrapped in a styled
              pill — see LanguageSelector for the a11y rationale
              (free keyboard nav, screen-reader combobox, iOS PWA
              native picker wheel). i18next.changeLanguage persists
              the choice via LANGUAGE_STORAGE_KEY automatically.
            */}
            <section className="border-t border-moor-100 pt-8">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-moor-500">
                {t('language.sectionTitle')}
              </h2>
              <p className="mt-1 text-sm text-moor-600">
                {t('language.sectionDescription')}
              </p>
              <div className="mt-3">
                <LanguageSelector />
              </div>
            </section>
            {/*
              Notifications — Batch 4 slice 4b step 3. Always visible
              regardless of profile state. The internal branching covers
              every device + permission combination:
                - Unsupported (no SW / no PushManager / no Notification API)
                - iOS not yet installed to home screen
                - Permission denied at the browser level
                - Off (supported, granted/default permission, not subscribed)
                - On (subscribed)
                - Changing (mid-flight toggle)
              SettingsPage chrome i18n landed in Phase 4b-N
              (14 Jun 2026); chip labels + DateInputs chrome translated
              in Phase 4b-final-A (14 Jun 2026). Validation error
              messages (onboarding.ts) remain English-only pending
              Phase 4b-final-B, which changes the lib API to return
              codes instead of strings.
            */}
            <section className="border-t border-moor-100 pt-8">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-moor-500">
                {t('push.sectionTitle')}
              </h2>
              <p className="mt-1 text-sm text-moor-600">
                {t('push.sectionDescription')}
              </p>
              <div className="mt-3">
                {pushStatus === null ? null : pushStatus.support ===
                  'unsupported' ? (
                  <p className="text-sm text-moor-600">
                    {t('push.statusUnsupported')}
                  </p>
                ) : pushStatus.support === 'needs-install' ? (
                  <p className="text-sm text-moor-600">
                    {t('push.iosNotInstalledHint')}
                  </p>
                ) : pushStatus.permission === 'denied' ? (
                  <p className="text-sm text-moor-600">
                    {t('push.permissionBlockedHelp')}
                  </p>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handlePushToggle}
                      disabled={isPushChanging}
                      className="rounded-full bg-moor-700 px-5 py-2 text-sm font-medium text-cream-50 transition-colors hover:bg-moor-800 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
                    >
                      {isPushChanging
                        ? pushStatus.subscribed
                          ? t('push.unsubscribing')
                          : t('push.subscribing')
                        : pushStatus.subscribed
                          ? t('push.turnOff')
                          : t('push.turnOn')}
                    </button>
                    <p className="mt-2 text-sm text-moor-600">
                      {pushStatus.subscribed
                        ? t('push.statusOn')
                        : t('push.statusOff')}
                    </p>
                  </>
                )}
                {pushError !== null ? (
                  <p
                    role="alert"
                    className="mt-2 text-sm"
                    style={{ color: '#9a2a2a' }}
                  >
                    {pushError}
                  </p>
                ) : null}
              </div>
            </section>
            {/*
              Clear chat history — Batch 6 (16 Jun 2026). Sits between
              Notifications and Account because it's a destructive
              privacy control that's distinct from sign-out (sign-out
              is reversible by signing back in; clearing chat is
              permanent). Native window.confirm() for the confirmation
              gate — single destructive action, no dependency on a
              dialog component, works identically across the supported
              browsers including iOS PWA where Mick tested.
              Server-side soft-delete is the source of truth (DELETE
              /v1/chat/history → softDeleteAllMessages); the
              localStorage clear and TanStack cache seed are
              consequence steps so MePage doesn't fold the now-stale
              local cache back over the empty server result. See
              handleClearChat above for the full step ordering and
              the bug-fix history.
            */}
            <section className="border-t border-moor-100 pt-8">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-moor-500">
                {t('settingsPage.clearChat.sectionTitle')}
              </h2>
              <p className="mt-1 text-sm text-moor-600">
                {t('settingsPage.clearChat.sectionDescription', {
                  personaName: persona.personaName,
                })}
              </p>
              <button
                type="button"
                onClick={handleClearChat}
                disabled={isClearingChat}
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-cream-100 px-4 py-2 text-sm font-medium text-moor-900 ring-1 ring-moor-200 transition-colors hover:bg-moor-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {isClearingChat
                  ? t('settingsPage.clearChat.clearing')
                  : t('settingsPage.clearChat.button')}
              </button>
              {clearChatError !== null ? (
                <p
                  role="alert"
                  className="mt-2 text-sm"
                  style={{ color: '#9a2a2a' }}
                >
                  {clearChatError}
                </p>
              ) : null}
            </section>
            {/*
              Sign-out section — always visible, regardless of whether
              the user has completed profile capture. Separate visual
              section so it doesn't blend in with editable settings.
            */}
            <section className="border-t border-moor-100 pt-8">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-moor-500">
                {t('settingsPage.account.sectionTitle')}
              </h2>
              <p className="mt-1 text-sm text-moor-600">
                {t('settingsPage.account.sectionDescription')}
              </p>
              <button
                type="button"
                onClick={handleSignOut}
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-cream-100 px-4 py-2 text-sm font-medium text-moor-900 ring-1 ring-moor-200 transition-colors hover:bg-moor-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                {t('settingsPage.account.signOut')}
              </button>
            </section>
          </div>
        </main>
        {/* Save/cancel footer — only relevant when the form is visible. */}
        {original ? (
          <div
            className="border-t border-moor-100 bg-cream-50"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="container-narrow flex flex-col gap-2 px-4 py-3">
              {updateProfileMutation.isError ? (
                <p
                  role="alert"
                  className="text-sm font-medium text-moor-900"
                  style={{ color: '#9a2a2a' }}
                >
                  {t('settingsPage.saveError')}
                </p>
              ) : null}
              <div className="flex items-center gap-3">
                <Link
                  to="/me"
                  className="rounded-full px-4 py-2 text-sm font-medium text-moor-600 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
                >
                  {t('settingsPage.cancel')}
                </Link>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!canSave}
                  className="ml-auto rounded-full bg-moor-700 px-5 py-2 text-sm font-medium text-cream-50 transition-colors hover:bg-moor-800 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
                >
                  {isSaving
                    ? t('settingsPage.saving')
                    : t('settingsPage.save')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
